import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { NextParsedUrlQuery } from 'next/dist/server/request-meta';
import { Article as ArticleModel, TOCItem } from 'constants/blog';
import { getArticlePageUrl } from 'constants/pages';
import { PRODUCT_BLOG, SITE_URL } from 'constants/products';
import { getAllPosts } from 'services/server/sanity/groq/getAllPosts';
import { getAllPostSlugs } from 'services/server/sanity/groq/getPostSlugs';
import { getSinglePost } from 'services/server/sanity/groq/getSinglePost';
import { generateArticleJsonLd } from 'utils/shared/json-ld/article';
import { generateBreadcrumbListJsonLd } from 'utils/shared/json-ld/breadcrumbs';
import { combineJsonLdSchemas } from 'utils/shared/json-ld/combineJsonLdSchemas';
import { generateFaqJsonLd } from 'utils/shared/json-ld/faq';
import { toSlug } from 'utils/shared/string/generateSlug';
import { calculateReadingTime } from 'utils/shared/blog/calculateReadingTime';
import { extractFaqs } from 'utils/shared/blog/extractFaqs';
import { extractTableOfContents } from 'utils/shared/blog/extractTableOfContents';
import ArticlePage from 'screens/Blog/ArticlePage';

const HOUR = 3600;
const CACHE_TIME = HOUR;

// Helper to determine if running in staging environment
const isStaging = () => process.env.NEXT_PUBLIC_ENABLE_DRAFT_MODE === 'true';

function safeStringify(obj: object, cache = new WeakSet()) {
  return JSON.stringify(obj, (_key, value) => {
    // If type of value is an object or array
    if (typeof value === 'object' && value !== null) {
      // If the value has been JSON stringified before, return a placeholder
      if (cache.has(value)) {
        return; // or you could return a placeholder string, e.g., "[Circular]"
      }
      // Add the current item to the cache
      cache.add(value);
    }
    return value;
  });
}

export const getStaticProps: GetStaticProps<{
  articles: ArticleModel[];
  article: ArticleModel;
  jsonLd?: string;
  readingTime: number;
  tableOfContents: TOCItem[];
}> = async ({ params }) => {
  const { slug } = params as NextParsedUrlQuery;

  const article = await getSinglePost(slug as string);

  if (!article) {
    return {
      notFound: true,
    };
  }

  const { posts } = await getAllPosts(3, 0, []);

  // Calculate reading time from article body
  const { minutes: readingTime, words: wordCount } = calculateReadingTime(article.body);

  // Extract table of contents from article body
  const tableOfContents = extractTableOfContents(article.body);

  const authorSocialLinks = article.author?.socialLinks ?? {};
  const authorSameAs = [
    authorSocialLinks.linkedin,
    authorSocialLinks.twitter,
    authorSocialLinks.facebook,
    authorSocialLinks.instagram,
    authorSocialLinks.youtube,
    authorSocialLinks.tiktok,
  ].filter((link): link is string => Boolean(link));

  const authorSlug = article.author?.name ? toSlug(article.author.name) : '';
  const authorPageUrl = authorSlug ? `${SITE_URL}/author/${authorSlug}` : undefined;

  const blogPostJsonLd = generateArticleJsonLd({
    headline: article.title,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    description: article.previewDescription,
    author: {
      name: article.author.name,
      ...(authorPageUrl && { url: authorPageUrl }),
      ...(authorSameAs.length > 0 && { sameAs: authorSameAs }),
    },
    image: [article?.mainImage],
    /**
     * @note need to transform from portable text for this to work
     */
    // articleBody: data.body,
    publisher: {
      name: 'Bounce',
      url: 'https://www.bounce.game',
      logo: 'https://www.bounce.game/icons/logo.png',
    },
    mainEntityOfPage: 'https://www.bounce.game' + getArticlePageUrl(article.slug),
    wordCount,
    timeRequired: readingTime,
    speakable: { cssSelector: ['h1', '.article-tldr'] },
  });

  const extractedFaqs = extractFaqs(article.body);
  const faqJsonLd =
    extractedFaqs.length > 0
      ? generateFaqJsonLd({
          items: extractedFaqs.map((faq) => ({ question: faq.question, answer: faq.answer })),
        })
      : null;

  const articleUrl = `${SITE_URL}${getArticlePageUrl(article.slug)}`;
  const breadcrumbsJsonLd = generateBreadcrumbListJsonLd({
    items: [
      { id: SITE_URL, name: 'Home' },
      { id: PRODUCT_BLOG.url, name: 'Blog' },
      { id: articleUrl, name: article.title },
    ],
  });

  const combinedSchemas = combineJsonLdSchemas(
    faqJsonLd
      ? [blogPostJsonLd, faqJsonLd, breadcrumbsJsonLd]
      : [blogPostJsonLd, breadcrumbsJsonLd],
  );

  return {
    props: {
      article: JSON.parse(safeStringify(article)),
      articles: JSON.parse(safeStringify(posts)),
      jsonLd: JSON.stringify(combinedSchemas),
      readingTime,
      tableOfContents: JSON.parse(safeStringify(tableOfContents)),
    },
    // 1 second in staging for immediate draft updates, 1 hour in production
    revalidate: isStaging() ? 1 : CACHE_TIME,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { posts } = await getAllPostSlugs();

  const paths = posts.map((article: any) => ({ params: { slug: article.slug } }));

  return {
    paths,
    fallback: 'blocking',
  };
};

const Article: NextPage<{
  article: ArticleModel;
  articles: ArticleModel[];
  jsonLd?: string;
  readingTime: number;
  tableOfContents: TOCItem[];
}> = (props) => <ArticlePage {...props} />;

export default Article;
